/**
 * Allocation closure verdict.
 *
 * The one thing a verifier looks for: did the period close, and if not, where.
 * Three checks reduce to a single verdict, because a reader should not have to
 * assemble one from three numbers.
 *
 * The important distinction is between the two ways a station can be left
 * short. If volume was still banked while a station went unfilled, the
 * allocation is wrong and this is red. If the lot queue simply ran dry, the
 * dairies did not produce enough to cover what was claimed at the stations —
 * that is a real operational condition and a question about the claim, not a
 * defect in the arithmetic, so it reads as a warning with the figure named.
 * Collapsing those two into one "failed" state would send someone hunting for
 * a bug that isn't there.
 */

import { Decimal } from "@compliance/domain";
import { Alert, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle } from "@tabler/icons-react";
import type { AllocationClosure } from "../api/types";

type Verdict = { color: string; label: string; detail: string };

function verdictFor(closure: AllocationClosure, banked: string): Verdict {
  if (closure.station_closure === "residual") {
    return {
      color: "red",
      label: "Does not close",
      detail: `${closure.unexplained_stations} station${
        closure.unexplained_stations === 1 ? "" : "s"
      } left unfilled while volume was still banked. The allocation is wrong, not the inputs.`,
    };
  }
  if (closure.target_closure === "mismatch") {
    return {
      color: "red",
      label: "Does not close",
      detail: "Allocated volume disagrees with the period's claimed capacity.",
    };
  }
  if (closure.station_closure === "queue_exhausted") {
    return {
      color: "orange",
      label: "Closes short",
      detail: `Stations claimed ${fmt(
        closure.residual_total.value,
      )} MMBtu more than the dairies produced. Every available MMBtu is allocated — the shortfall is in the claim, not the arithmetic.`,
    };
  }
  return {
    color: "teal",
    label: "Closes",
    detail: `Every station fully allocated, no lot over-drawn. ${fmt(
      banked,
    )} MMBtu carried forward.`,
  };
}

/** Thousands-separated from a decimal string; never `Number()` on the figure. */
function fmt(value: string): string {
  try {
    return new Decimal(value).toDecimalPlaces(3).toNumber().toLocaleString("en-US", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  } catch {
    return value;
  }
}

export function ClosureBanner({
  closure,
  banked,
}: {
  closure: AllocationClosure;
  banked: string;
}) {
  const verdict = verdictFor(closure, banked);
  const icon =
    verdict.color === "teal" ? (
      <IconCircleCheck size={18} />
    ) : verdict.color === "orange" ? (
      <IconInfoCircle size={18} />
    ) : (
      <IconAlertTriangle size={18} />
    );

  return (
    <Alert color={verdict.color} variant="light" icon={icon} data-test="closure-banner">
      <Stack gap={6}>
        <Group gap="xs" wrap="nowrap">
          <Text fw={700} size="sm">
            {verdict.label}
          </Text>
          <Text size="sm" c="dimmed">
            · {verdict.detail}
          </Text>
        </Group>
        <Group gap="lg" wrap="wrap">
          <Check label="Station closure" state={closure.station_closure} />
          <Check label="Target closure" state={closure.target_closure} />
        </Group>
      </Stack>
    </Alert>
  );
}

const STATE_LABEL: Record<string, string> = {
  ok: "ok",
  residual: "unexplained residual",
  queue_exhausted: "queue exhausted",
  mismatch: "mismatch",
};

function Check({ label, state }: { label: string; state: string }) {
  const ok = state === "ok";
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="xs" ff="monospace" fw={600} c={ok ? "teal" : "orange"}>
        {STATE_LABEL[state] ?? state}
      </Text>
    </Group>
  );
}
